import React, { useState, useEffect } from 'react';
import SearchableSelect from './SearchableSelect';
import AddButton from './AddButton';
import { AddVehicleModal, AddCarrierModal, AddDriverModal, AddTrailerModal, AddConvoyeurModal } from './modals';
import weighingService from '@/services/weighing';

interface VehicleInfoSectionProps {
  formData: {
    vehicule: string;
    remorque: string;
    transporteur: string;
    chauffeur: string;
    convoyeur?: string;
  };
  onInputChange: (field: string, value: any) => void;
}

const VehicleInfoSection: React.FC<VehicleInfoSectionProps> = ({ formData, onInputChange }) => {
  // États pour les modales
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false);
  const [isCarrierModalOpen, setIsCarrierModalOpen] = useState(false);
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [isConvoyeurModalOpen, setIsConvoyeurModalOpen] = useState(false);

  // États pour les erreurs de soumission
  const [vehicleSubmitError, setVehicleSubmitError] = useState<string>('');

  // États pour les données
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [trailers, setTrailers] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [convoyeurs, setConvoyeurs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isConvoyeurPresent, setIsConvoyeurPresent] = useState(false);


  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les véhicules
      const vehiclesResponse = await weighingService.getCamions();
      
      
      if (vehiclesResponse && vehiclesResponse.results) {
        setVehicles(vehiclesResponse.results || []);
      }

      // Charger les remorques
      const trailersResponse = await weighingService.getRemorques();
      
      if (trailersResponse && trailersResponse.results) {
        setTrailers(trailersResponse.results || []);
      }

      // Charger les transporteurs
      const carriersResponse = await weighingService.getTransporteurs();
      if (carriersResponse && carriersResponse.results) {
        setCarriers(carriersResponse.results || []);
      }

      // Charger les chauffeurs
      const driversResponse = await weighingService.getChauffeurs();
      if (driversResponse && driversResponse.results) {
        setDrivers(driversResponse.results || []);
      }

      // Charger les convoyeurs
      try {
        const convoyeursResponse = await weighingService.getConvoyeurs();
        if (convoyeursResponse && convoyeursResponse.results) {
          setConvoyeurs(convoyeursResponse.results || []);
        }
      } catch (error) {
        console.error('Convoyeurs non disponibles:', error);
        setConvoyeurs([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convertir les données en format pour SearchableSelect
  const vehicleOptions = vehicles.map(v => ({
    value: v.id.toString(),
    label: `${v.immatriculation} - ${v.marque} ${v.modele || ''}`
  }));

  const trailerOptions = trailers.map(t => ({
    value: t.id.toString(),
    label: `${t.immatriculation} - ${t.marque} (Capacité: ${t.capacite || ''}kg)`
  }));

  const carrierOptions = carriers.map(c => ({
    value: c.id.toString(),
    label: `${c.nom} - ${c.zone || ''}`
  }));

  const driverOptions = drivers.map(d => ({
    value: d.id.toString(),
    label: `${d.prenom} ${d.nom} - ${d.permis}`
  }));

  const convoyeurOptions = convoyeurs.map(c => ({
    value: c.id.toString(),
    label: `${c.prenom} ${c.nom} - ${c.telephone || ''}`
  }));

  // Gestionnaires pour les modales
  const handleAddVehicle = async (vehicleData: any) => {
    try {
      setVehicleSubmitError(''); // Effacer l'erreur précédente
      await weighingService.createCamion(vehicleData);
      
      await loadData(); // Recharger les données
      setIsVehicleModalOpen(false);
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout du véhicule:', error);
      
      // Extraire le message d'erreur
      let errorMessage = 'Une erreur est survenue lors de l\'ajout du véhicule';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        // Si c'est une erreur de validation, afficher les erreurs
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setVehicleSubmitError(errorMessage);
    }
  };

  const handleAddTrailer = async (trailerData: any) => {
    try {
      await weighingService.createRemorque(trailerData);
      
      await loadData(); // Recharger les données
      setIsTrailerModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la remorque:', error);
    }
  };

  const handleAddCarrier = async (carrierData: any) => {
    try {
      await weighingService.createTransporteur(carrierData);
      
      await loadData(); // Recharger les données
      setIsCarrierModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du transporteur:', error);
    }
  };

  const handleAddDriver = async (driverData: any) => {
    try {
      await weighingService.createChauffeur(driverData);
   
      await loadData(); // Recharger les données
      setIsDriverModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du chauffeur:', error);
    }
  };

  const handleAddConvoyeur = async (convoyeurData: any) => {
    try {
      await weighingService.createConvoyeur(convoyeurData);
      
      await loadData(); // Recharger les données
      setIsConvoyeurModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du convoyeur:', error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 text-sm">Informations Véhicule</h3>
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          <span className="ml-2 text-sm text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        <div className="flex items-center space-x-1">
          <SearchableSelect
            value={formData.vehicule}
            onChange={(value) => onInputChange('vehicule', value)}
            options={vehicleOptions}
            placeholder="Sélectionner un véhicule"
            className="flex-1 z-[1000]"
          />
          <AddButton 
            tooltip="Ajouter un nouveau véhicule" 
            onClick={() => setIsVehicleModalOpen(true)}
          />
        </div>

        <div className="flex items-center space-x-1">
          <SearchableSelect
            value={formData.remorque}
            onChange={(value) => onInputChange('remorque', value)}
            options={trailerOptions}
            placeholder="Sélectionner une remorque"
            className="flex-1"
          />
          <AddButton 
            tooltip="Ajouter une nouvelle remorque" 
            onClick={() => setIsTrailerModalOpen(true)}
          />
        </div>

        <div className="flex items-center space-x-1">
          <SearchableSelect
            value={formData.transporteur}
            onChange={(value) => onInputChange('transporteur', value)}
            options={carrierOptions}
            placeholder="Sélectionner un transporteur"
            className="flex-1"
          />
          <AddButton 
            tooltip="Ajouter un nouveau transporteur" 
            onClick={() => setIsCarrierModalOpen(true)}
          />
        </div>

        <div className="flex items-center space-x-1">
          <SearchableSelect
            value={formData.chauffeur}
            onChange={(value) => onInputChange('chauffeur', value)}
            options={driverOptions}
            placeholder="Sélectionner un chauffeur"
            className="flex-1"
          />
          <AddButton 
            tooltip="Ajouter un nouveau chauffeur" 
            onClick={() => setIsDriverModalOpen(true)}
          />
        </div>

        {/* Section Convoyeur */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Convoyeur présent ?</label>
            <button
              type="button"
              onClick={() => setIsConvoyeurPresent(!isConvoyeurPresent)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                isConvoyeurPresent ? 'bg-orange-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isConvoyeurPresent ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {isConvoyeurPresent && (
            <div className="flex items-center space-x-1">
              <SearchableSelect
                value={formData.convoyeur || ''}
                onChange={(value) => onInputChange('convoyeur', value)}
                options={convoyeurOptions}
                placeholder="Sélectionner un convoyeur"
                className="flex-1"
              />
              <AddButton 
                tooltip="Ajouter un nouveau convoyeur" 
                onClick={() => setIsConvoyeurModalOpen(true)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modales */}
      <AddVehicleModal
        isOpen={isVehicleModalOpen}
        onClose={() => {
          setIsVehicleModalOpen(false);
          setVehicleSubmitError(''); // Effacer l'erreur lors de la fermeture
        }}
        onSave={handleAddVehicle}
        submitError={vehicleSubmitError}
      />

      <AddTrailerModal
        isOpen={isTrailerModalOpen}
        onClose={() => setIsTrailerModalOpen(false)}
        onSave={handleAddTrailer}
      />

      <AddCarrierModal
        isOpen={isCarrierModalOpen}
        onClose={() => setIsCarrierModalOpen(false)}
        onSave={handleAddCarrier}
      />

      <AddDriverModal
        isOpen={isDriverModalOpen}
        onClose={() => setIsDriverModalOpen(false)}
        onSave={handleAddDriver}
      />

      <AddConvoyeurModal
        isOpen={isConvoyeurModalOpen}
        onClose={() => setIsConvoyeurModalOpen(false)}
        onSave={handleAddConvoyeur}
      />
    </div>
  );
};

export default VehicleInfoSection;
